import React, { useMemo, useEffect, useRef, useState, useCallback } from "react";

interface CandlelightBackgroundProps {
  className?: string;
  count?: number;
}

interface Flame {
  id: number;
  x: number;
  y: number;
  size: number;
  opacity: number;
  flickerSpeed: number;
  flickerDelay: number;
  glowSize: number;
  driftX: number;
  driftY: number;
}

const CandlelightBackground: React.FC<CandlelightBackgroundProps> = ({
  className = "",
  count = 14,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const mouseRef = useRef({ x: -9999, y: -9999, active: false });
  const dimRef = useRef<number[]>([]);
  const rafRef = useRef<number>();

  const flames = useMemo<Flame[]>(() => {
    const result = Array.from({ length: count }, (_, i) => ({
      id: i,
      x: 5 + Math.random() * 90,
      y: 5 + Math.random() * 90,
      size: 8 + Math.random() * 18,
      opacity: 0.4 + Math.random() * 0.5,
      flickerSpeed: 1.5 + Math.random() * 3,
      flickerDelay: -(Math.random() * 5),
      glowSize: 80 + Math.random() * 180,
      driftX: -8 + Math.random() * 16,
      driftY: -15 + Math.random() * 10,
    }));
    dimRef.current = result.map(() => 0);
    return result;
  }, [count]);

  const [dimFactors, setDimFactors] = useState<number[]>(() => flames.map(() => 0));

  const animate = useCallback(() => {
    const container = containerRef.current;
    if (!container) {
      rafRef.current = requestAnimationFrame(animate);
      return;
    }

    const rect = container.getBoundingClientRect();
    const mouse = mouseRef.current;
    const influence = 200;
    const decay = 0.15;

    let changed = false;

    flames.forEach((flame, i) => {
      const cx = (flame.x / 100) * rect.width;
      const cy = (flame.y / 100) * rect.height;

      let target = 0;

      if (mouse.active) {
        const mx = mouse.x - rect.left;
        const my = mouse.y - rect.top;
        const dx = cx - mx;
        const dy = cy - my;
        const dist = Math.sqrt(dx * dx + dy * dy);

        if (dist < influence) {
          target = Math.max(0, 1 - dist / influence);
        }
      }

      const cur = dimRef.current[i];
      const next = cur + (target - cur) * decay;

      if (Math.abs(next - cur) > 0.002) {
        changed = true;
      }

      dimRef.current[i] = next;
    });

    if (changed) {
      setDimFactors([...dimRef.current]);
    }

    rafRef.current = requestAnimationFrame(animate);
  }, [flames]);

  useEffect(() => {
    rafRef.current = requestAnimationFrame(animate);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [animate]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      mouseRef.current = { x: e.clientX, y: e.clientY, active: true };
    };
    const handleMouseLeave = () => {
      mouseRef.current = { ...mouseRef.current, active: false };
    };
    window.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseleave", handleMouseLeave);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, []);

  return (
    <div ref={containerRef} className={`overflow-hidden pointer-events-none ${className}`}>
      {flames.map((flame, i) => {
        const dim = dimFactors[i] ?? 0;
        const brightness = 1 - dim * 0.95;

        return (
          <div
            key={flame.id}
            className="absolute"
            style={{
              left: `${flame.x}%`,
              top: `${flame.y}%`,
              filter: `brightness(${brightness})`,
              transition: 'filter 0.05s linear',
            }}
          >
            {/* Warm ambient glow */}
            <div
              className="absolute rounded-full"
              style={{
                width: flame.glowSize,
                height: flame.glowSize,
                left: `${-flame.glowSize / 2}px`,
                top: `${-flame.glowSize / 2}px`,
                background: `radial-gradient(circle, rgba(255, 160, 50, 0.15) 0%, rgba(200, 100, 20, 0.06) 40%, transparent 70%)`,
                opacity: flame.opacity * 0.7,
                animation: `candle-glow ${flame.flickerSpeed}s ease-in-out ${flame.flickerDelay}s infinite`,
              }}
            />
            {/* Flame core */}
            <div
              className="absolute"
              style={{
                width: flame.size,
                height: flame.size * 1.8,
                left: `${-flame.size / 2}px`,
                top: `${-flame.size}px`,
                background: `radial-gradient(ellipse at 50% 80%, rgba(255, 220, 100, 0.9) 0%, rgba(255, 160, 40, 0.6) 30%, rgba(255, 80, 10, 0.3) 60%, transparent 80%)`,
                borderRadius: '50% 50% 50% 50% / 60% 60% 40% 40%',
                opacity: flame.opacity,
                filter: `blur(${flame.size * 0.2}px)`,
                animation: `candle-flicker ${flame.flickerSpeed}s ease-in-out ${flame.flickerDelay}s infinite`,
                ["--drift-x" as string]: `${flame.driftX}px`,
                ["--drift-y" as string]: `${flame.driftY}px`,
              }}
            />
            {/* Inner bright core */}
            <div
              className="absolute"
              style={{
                width: flame.size * 0.4,
                height: flame.size * 0.7,
                left: `${-flame.size * 0.2}px`,
                top: `${-flame.size * 0.5}px`,
                background: `radial-gradient(ellipse, rgba(255, 255, 200, 0.8) 0%, rgba(255, 200, 80, 0.4) 50%, transparent 80%)`,
                borderRadius: '50% 50% 50% 50% / 60% 60% 40% 40%',
                opacity: flame.opacity,
                filter: `blur(${flame.size * 0.1}px)`,
                animation: `candle-flicker ${flame.flickerSpeed * 0.7}s ease-in-out ${flame.flickerDelay - 0.3}s infinite`,
              }}
            />
          </div>
        );
      })}

      {/* Warm overall wash */}
      <div
        className="absolute inset-0"
        style={{
          background: 'radial-gradient(ellipse at 50% 40%, rgba(180, 100, 30, 0.08) 0%, transparent 70%)',
        }}
      />

      <style>{`
        @keyframes candle-flicker {
          0%, 100% { 
            transform: scaleY(1) scaleX(1) translateY(0);
            opacity: 1;
          }
          15% { 
            transform: scaleY(1.1) scaleX(0.9) translateY(-2px);
            opacity: 0.85;
          }
          30% { 
            transform: scaleY(0.85) scaleX(1.05) translateY(1px) rotate(2deg);
            opacity: 0.95;
          }
          50% { 
            transform: scaleY(1.05) scaleX(0.95) translateY(-1px) rotate(-1deg);
            opacity: 0.75;
          }
          70% { 
            transform: scaleY(0.9) scaleX(1.1) translateY(0) rotate(1deg);
            opacity: 0.9;
          }
          85% { 
            transform: scaleY(1.15) scaleX(0.85) translateY(-3px);
            opacity: 0.8;
          }
        }
        @keyframes candle-glow {
          0%, 100% { 
            transform: scale(1);
            opacity: 1;
          }
          25% { 
            transform: scale(1.05);
            opacity: 0.8;
          }
          50% { 
            transform: scale(0.95);
            opacity: 0.9;
          }
          75% { 
            transform: scale(1.08);
            opacity: 0.75;
          }
        }
      `}</style>
    </div>
  );
};

export default CandlelightBackground;
